import { z } from "zod";
import { CropSchema, EffectsSchema, FillSchema, HyperlinkSchema, StrokeSchema, TransformSchema } from "./primitives";
import { GeometrySchema } from "./geometry";
import { InsetsSchema, TextBodySchema } from "./text";
import { MediaSourceSchema, StartModeSchema } from "./media";

export const PlaceholderTypeSchema = z.enum([
  "title", "ctrTitle", "subTitle", "body", "obj", "pic", "tbl", "chart", "dgm", "media",
  "clipArt", "sldNum", "dt", "ftr", "hdr", "sldImg",
]);
export type PlaceholderType = z.infer<typeof PlaceholderTypeSchema>;

const ElementBase = z.object({
  id: z.string(),
  name: z.string().optional(),
  /** cNvPr id in the source slide xml */
  sourceId: z.string().optional(),
  transform: TransformSchema,
  hidden: z.boolean().optional(),
  placeholder: z.object({ type: PlaceholderTypeSchema, idx: z.number().optional() }).optional(),
  hyperlink: HyperlinkSchema.optional(),
});

export const ShapeElementSchema = ElementBase.extend({
  kind: z.literal("shape"),
  geometry: GeometrySchema,
  fill: FillSchema.nullable(),
  stroke: StrokeSchema.nullable(),
  effects: EffectsSchema.nullable().default(null),
  text: TextBodySchema.nullable().default(null),
  /** where the text goes when not the shape's own box (SmartArt gives each shape one), in the same space as `transform` */
  textTransform: TransformSchema.optional(),
});
export type ShapeElement = z.infer<typeof ShapeElementSchema>;

export const TextBoxElementSchema = ElementBase.extend({
  kind: z.literal("textbox"),
  geometry: GeometrySchema,
  fill: FillSchema.nullable(),
  stroke: StrokeSchema.nullable(),
  effects: EffectsSchema.nullable().default(null),
  text: TextBodySchema,
  textTransform: TransformSchema.optional(),
});
export type TextBoxElement = z.infer<typeof TextBoxElementSchema>;

export const PictureElementSchema = ElementBase.extend({
  kind: z.literal("picture"),
  mediaId: z.string(),
  crop: CropSchema.optional(),
  geometry: GeometrySchema.optional(),
  stroke: StrokeSchema.nullable().default(null),
  effects: EffectsSchema.nullable().default(null),
});
export type PictureElement = z.infer<typeof PictureElementSchema>;

export const MediaElementSchema = ElementBase.extend({
  kind: z.literal("media"),
  mediaType: z.enum(["video", "audio"]),
  /** null => poster only (linked / missing / unplayable) */
  source: MediaSourceSchema.nullable(),
  /** mediaId of the poster image */
  poster: z.string().nullable(),
  geometry: GeometrySchema.optional(),
  stroke: StrokeSchema.nullable().default(null),
  effects: EffectsSchema.nullable().default(null),
  loop: z.boolean().default(false),
  mute: z.boolean().default(false),
  volume: z.number().min(0).max(1).default(0.8),
  trim: z.object({ startAt: z.number().default(0), endAt: z.number().nullable().default(null) }).default({}),
  fade: z.object({ in: z.number().default(0), out: z.number().default(0) }).default({}),
  bookmarks: z.array(z.object({ name: z.string(), time: z.number() })).default([]),
  hideWhenNotPlaying: z.boolean().default(false),
  playFullScreen: z.boolean().default(false),
  playAcrossSlides: z.number().int().min(1).default(1),
  isNarration: z.boolean().default(false),
  startMode: StartModeSchema.default("manual"),
});
export type MediaElement = z.infer<typeof MediaElementSchema>;

export const LineElementSchema = ElementBase.extend({
  kind: z.literal("line"),
  geometry: GeometrySchema,
  stroke: StrokeSchema,
});
export type LineElement = z.infer<typeof LineElementSchema>;

export const TableCellSchema = z.object({
  covered: z.literal(true).optional(),
  rowSpan: z.number().int().optional(),
  colSpan: z.number().int().optional(),
  text: TextBodySchema,
  fill: FillSchema.nullable(),
  borders: z.object({
    l: StrokeSchema.nullable(),
    r: StrokeSchema.nullable(),
    t: StrokeSchema.nullable(),
    b: StrokeSchema.nullable(),
  }),
  insets: InsetsSchema,
});
export type TableCell = z.infer<typeof TableCellSchema>;

export const TableElementSchema = ElementBase.extend({
  kind: z.literal("table"),
  colWidths: z.array(z.number()),
  rowHeights: z.array(z.number()),
  cells: z.array(z.array(TableCellSchema)),
});
export type TableElement = z.infer<typeof TableElementSchema>;

export const UnsupportedElementSchema = ElementBase.extend({
  kind: z.literal("unsupported"),
  what: z.enum(["chart", "ole", "smartart", "model3d", "ink", "other"]),
  fallbackMediaId: z.string().optional(),
  label: z.string(),
});
export type UnsupportedElement = z.infer<typeof UnsupportedElementSchema>;

export type GroupElement = z.infer<typeof ElementBase> & {
  kind: "group";
  childSpace: { x: number; y: number; w: number; h: number };
  children: Element[];
};

export type Element =
  | ShapeElement
  | TextBoxElement
  | PictureElement
  | MediaElement
  | GroupElement
  | LineElement
  | TableElement
  | UnsupportedElement;

export const GroupElementSchema = ElementBase.extend({
  kind: z.literal("group"),
  childSpace: z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() }),
  children: z.lazy(() => z.array(ElementSchema)),
});

export const ElementSchema: z.ZodType<Element, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.discriminatedUnion("kind", [
    ShapeElementSchema,
    TextBoxElementSchema,
    PictureElementSchema,
    MediaElementSchema,
    GroupElementSchema,
    LineElementSchema,
    TableElementSchema,
    UnsupportedElementSchema,
  ]),
);
